import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CampaignService } from '../../services/campaign.service';
import {
  isValidCtaUrl,
  mapSuccessApiToModal,
  PublicCommonWebsiteContentApi,
  shouldShowSuccessCta,
} from '../campaign-editor/optional-setting/common-website-content.mapper';
import { ApiResponse } from '../../shared/types';
import {
  readSmsSuccessContact,
  replaceSuccessPageMergeFields,
} from '../campaign-editor/optional-setting/success-page-merge-fields.util';
import {
  readSmsCampaignTrackingToken,
  SMS_CAMPAIGN_TRACKING_TOKEN_QUERY_KEY,
} from '../campaign-editor/optional-setting/sms-campaign-tracking.util';
import {
  buildResendStatusText,
  parseResendCooldownSeconds,
} from '../campaign-editor/optional-setting/success-page-resend.util';
import {
  DEFAULT_SUCCESS_PAGE_DATA,
  SuccessPageModalData,
} from '../campaign-editor/optional-setting/create-modal-success-page/create-modal-success-page.component';

interface SmsSuccessNavigationState {
  contactValue?: string;
  successPage?: SuccessPageModalData;
  landingLogo?: string;
}

const MISSING_CONTACT_ERROR =
  'We could not find the contact this offer was sent to. Please submit the form again.';
const RESEND_FAILED_ERROR = 'Unable to resend. Please try again.';

@Component({
  selector: 'app-sms-campaign-success-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sms-campaign-success-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sms-campaign-success-page.component.scss',
})
export class SmsCampaignSuccessPageComponent implements OnInit, OnDestroy {
  successPage: SuccessPageModalData = { ...DEFAULT_SUCCESS_PAGE_DATA };
  loading = false;
  error: string | null = null;

  resending = false;
  resendError = '';
  resendMessage = '';
  cooldownRemainingSeconds = 0;
  attemptsUsed = 0;

  private campaignId = '';
  private contactValue = '';
  private landingLogo = '';
  private formInputType: 'email' | 'phone' = 'phone';
  private cooldownInitialized = false;
  private routeSub?: Subscription;
  private resendSub?: Subscription;
  private cooldownTimerId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private route: ActivatedRoute,
    private campaignService: CampaignService
  ) {}

  ngOnInit(): void {
    const navigationState = this.readNavigationState();

    // switchMap so a campaignId change abandons the previous content request: a late
    // response from the old campaign must never overwrite the current one's page,
    // contact type or cooldown.
    this.routeSub = this.route.queryParamMap
      .pipe(
        switchMap(params => {
          const campaignId = params.get('campaignId')?.trim();
          if (!campaignId) {
            this.error = 'Campaign ID is missing from the link.';
            return EMPTY;
          }

          this.resetForCampaign(campaignId, navigationState);
          this.loading = true;
          this.error = null;

          return this.campaignService.getPublicCommonWebsiteContent(campaignId).pipe(
            catchError(err => {
              this.handleContentError(err);
              return EMPTY;
            })
          );
        })
      )
      .subscribe(response => this.handleContentLoaded(response));
  }

  /** Clears every per-campaign field so a campaignId change cannot inherit stale state. */
  private resetForCampaign(campaignId: string, navigationState: SmsSuccessNavigationState): void {
    this.clearCooldownTimer();
    this.resendSub?.unsubscribe();
    this.resendSub = undefined;

    this.campaignId = campaignId;
    this.successPage = { ...DEFAULT_SUCCESS_PAGE_DATA };
    this.landingLogo = '';
    this.formInputType = 'phone';
    this.cooldownInitialized = false;
    this.cooldownRemainingSeconds = 0;
    this.attemptsUsed = 0;
    this.resending = false;
    this.resendError = '';
    this.resendMessage = '';

    this.contactValue = readSmsSuccessContact(campaignId, navigationState);
    this.applyNavigationState(navigationState);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.resendSub?.unsubscribe();
    this.clearCooldownTimer();
  }

  getSuccessPreviewLogo(): string {
    if (this.successPage.reuseLandingBrand) {
      return this.landingLogo;
    }
    return this.successPage.logoUrl || '';
  }

  getSuccessPreviewText(text: string): string {
    return replaceSuccessPageMergeFields(text, this.contactValue);
  }

  getSuccessResendStatusText(): string {
    return buildResendStatusText({
      cooldownText: this.successPage.cooldownText,
      cooldownRemainingSeconds: this.cooldownRemainingSeconds,
      attemptsLeft: this.getAttemptsLeft(),
    });
  }

  /** Resend attempts still allowed by the campaign's configured `max_attempts`. */
  getAttemptsLeft(): number {
    const maxAttempts = Number(this.successPage.maxAttempts);
    const total = Number.isFinite(maxAttempts) && maxAttempts > 0 ? Math.floor(maxAttempts) : 0;
    return Math.max(0, total - this.attemptsUsed);
  }

  canResend(): boolean {
    return (
      this.cooldownInitialized &&
      !this.resending &&
      this.cooldownRemainingSeconds <= 0 &&
      this.getAttemptsLeft() > 0 &&
      Boolean(this.campaignId) &&
      Boolean(this.contactValue)
    );
  }

  onResend(): void {
    this.resendMessage = '';

    if (!this.canResend()) {
      if (!this.contactValue) {
        this.resendError = MISSING_CONTACT_ERROR;
      }
      return;
    }

    this.resendError = '';
    this.resending = true;

    const payload: { campaign_id: string; recipient: string; tracking_token?: string } = {
      campaign_id: this.campaignId,
      recipient: this.buildRecipient(),
    };

    const trackingToken = this.resolveTrackingToken();
    if (trackingToken) {
      payload.tracking_token = trackingToken;
    }

    this.resendSub?.unsubscribe();
    this.resendSub = this.campaignService.sendPublicCommonWebsiteOffer(payload).subscribe({
      next: response => {
        this.resending = false;
        if (!response.status) {
          this.resendError = response.message || RESEND_FAILED_ERROR;
          return;
        }

        this.attemptsUsed += 1;
        this.resendMessage = 'Sent again. Check your messages.';
        this.startCooldown();
      },
      error: err => {
        this.resending = false;
        this.resendError = err?.error?.message || RESEND_FAILED_ERROR;
      },
    });
  }

  shouldShowCta(): boolean {
    return shouldShowSuccessCta(this.successPage);
  }

  onCtaClick(): void {
    const url = this.successPage.ctaUrl?.trim();
    if (!this.shouldShowCta() || !isValidCtaUrl(url)) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private readNavigationState(): SmsSuccessNavigationState {
    return (history.state ?? {}) as SmsSuccessNavigationState;
  }

  private applyNavigationState(state: SmsSuccessNavigationState): void {
    if (!this.contactValue && state.contactValue?.trim()) {
      this.contactValue = state.contactValue.trim();
    }
    if (state.landingLogo) {
      this.landingLogo = state.landingLogo;
    }
    if (state.successPage) {
      // Rendered immediately so the card is not blank, but the cooldown deliberately
      // waits for the API: this copy comes from the landing page's cache and may carry
      // an out-of-date cooldown for the campaign.
      this.successPage = { ...state.successPage };
    }
  }

  /**
   * The offer was just delivered when this page opened, so Resend stays on cooldown
   * until the configured window has elapsed. Runs once, only after the API has settled,
   * so the countdown is always sized by the campaign's current configuration.
   */
  private startInitialCooldown(): void {
    if (this.cooldownInitialized) {
      return;
    }
    this.cooldownInitialized = true;
    if (this.successPage.showResend) {
      this.startCooldown();
    }
  }

  private startCooldown(): void {
    this.clearCooldownTimer();
    this.cooldownRemainingSeconds = parseResendCooldownSeconds(this.successPage.cooldownText);

    this.cooldownTimerId = setInterval(() => {
      this.cooldownRemainingSeconds = Math.max(0, this.cooldownRemainingSeconds - 1);
      if (this.cooldownRemainingSeconds <= 0) {
        this.clearCooldownTimer();
      }
    }, 1000);
  }

  private clearCooldownTimer(): void {
    if (this.cooldownTimerId !== null) {
      clearInterval(this.cooldownTimerId);
      this.cooldownTimerId = null;
    }
  }

  /** Mirrors the landing page: phone contacts are sent as digits, emails verbatim. */
  private buildRecipient(): string {
    const contact = this.contactValue.trim();
    if (this.formInputType === 'email' || contact.includes('@')) {
      return contact;
    }
    return contact.replace(/\D/g, '') || contact;
  }

  private resolveTrackingToken(): string {
    const fromUrl = this.route.snapshot.queryParamMap
      .get(SMS_CAMPAIGN_TRACKING_TOKEN_QUERY_KEY)
      ?.trim();
    if (fromUrl) {
      return fromUrl;
    }

    if (this.campaignId) {
      return readSmsCampaignTrackingToken(this.campaignId);
    }

    return '';
  }

  private handleContentLoaded(response: ApiResponse<PublicCommonWebsiteContentApi>): void {
    this.loading = false;
    if (!response.status) {
      this.error = response.message || 'Unable to load success page.';
      return;
    }

    if (response.data?.landing_page?.logo && !this.landingLogo) {
      this.landingLogo = response.data.landing_page.logo;
    }

    if (response.data?.landing_page?.form_input_type) {
      this.formInputType =
        response.data.landing_page.form_input_type === 'email' ? 'email' : 'phone';
    }

    if (response.data?.success_page) {
      this.successPage = mapSuccessApiToModal(response.data.success_page);
    } else if (!this.successPage.title?.trim()) {
      this.error = 'Success page content is not available.';
      return;
    }

    this.startInitialCooldown();
  }

  private handleContentError(err: any): void {
    this.loading = false;
    if (!this.successPage.title?.trim()) {
      this.error = err?.error?.message || 'Unable to load success page.';
      return;
    }

    // Navigation-state content is all we have; size the cooldown from it rather than
    // leaving Resend permanently disabled.
    this.startInitialCooldown();
  }
}
